import { deleteToken, getToken, onMessage, Unsubscribe } from "firebase/messaging";
import {
  firebaseConfig,
  firebaseVapidKey,
  getFirebaseMessaging,
  isFirebaseConfigured,
} from "../../../config/firebase";
import { ForegroundNotification } from "../type/notification.types";

const SERVICE_WORKER_PATH = "/firebase-messaging-sw.js";

/** True when the current browser can register a SW and show notifications. */
export const isPushSupported = (): boolean =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "Notification" in window &&
  "PushManager" in window;

/**
 * Builds the SW registration URL with the Firebase config as query params, so
 * the worker can initialize Firebase without the values being hard-coded in the
 * static file. Only the public web config is included (never the VAPID key).
 */
const buildServiceWorkerUrl = (): string => {
  const params = new URLSearchParams();
  Object.entries(firebaseConfig).forEach(([key, value]) => {
    if (value) params.set(key, String(value));
  });
  return `${SERVICE_WORKER_PATH}?${params.toString()}`;
};

let registrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;

/**
 * Registers (or reuses) the FCM service worker. Resolves to `null` when push is
 * unsupported or Firebase is unconfigured. The result is cached for reuse.
 */
export const registerPushServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!isPushSupported() || !isFirebaseConfigured()) return null;

  if (!registrationPromise) {
    registrationPromise = navigator.serviceWorker
      .register(buildServiceWorkerUrl())
      .catch((error) => {
        console.error("[push] Failed to register service worker:", error);
        registrationPromise = null;
        return null;
      });
  }

  return registrationPromise;
};

/**
 * Requests the OS/browser notification permission, returning the resulting
 * state. Does not throw — a blocked prompt simply resolves to "denied".
 */
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!isPushSupported()) return "denied";
  if (Notification.permission === "granted") return "granted";

  try {
    return await Notification.requestPermission();
  } catch (error) {
    console.error("[push] Notification permission request failed:", error);
    return "denied";
  }
};

/**
 * Obtains the FCM registration token for this browser. Requires that
 * permission has already been granted. Returns `null` on any failure.
 */
export const fetchFcmToken = async (): Promise<string | null> => {
  if (!firebaseVapidKey) {
    console.error(
      "[push] REACT_APP_FIREBASE_VAPID_KEY is missing — cannot request an FCM token.",
    );
    return null;
  }

  const messaging = await getFirebaseMessaging();
  if (!messaging) return null;

  const registration = await registerPushServiceWorker();
  if (!registration) return null;

  try {
    const token = await getToken(messaging, {
      vapidKey: firebaseVapidKey,
      serviceWorkerRegistration: registration,
    });
    return token || null;
  } catch (error) {
    console.error("[push] Failed to obtain FCM token:", error);
    return null;
  }
};

/** Deletes the current FCM token (e.g. on logout / disabling notifications). */
export const deleteFcmToken = async (): Promise<boolean> => {
  const messaging = await getFirebaseMessaging();
  if (!messaging) return false;

  try {
    return await deleteToken(messaging);
  } catch (error) {
    console.error("[push] Failed to delete FCM token:", error);
    return false;
  }
};

/**
 * Subscribes to messages received while the app is in the foreground. Returns
 * an unsubscribe function (a no-op when messaging is unavailable).
 */
export const listenToForegroundMessages = async (
  handler: (notification: ForegroundNotification) => void,
): Promise<Unsubscribe> => {
  const messaging = await getFirebaseMessaging();
  if (!messaging) return () => undefined;

  return onMessage(messaging, (payload) => {
    const notification = payload.notification ?? {};
    const data = payload.data ?? {};

    handler({
      title: notification.title ?? data.title ?? "Lahent",
      body: notification.body ?? data.body ?? "",
      link: data.link ?? data.click_action,
      raw: payload,
    });
  });
};
